import { useCallback, useEffect, useRef, useState } from 'react';
import { onSensitiveMemoryCacheClear } from './sensitive-cache-lifecycle';

type Asset = { url: string; expiresAt: string; fileName: string; mimeType: string };
type MediaRepository = {
  open(
    id: string,
    manifestId: string,
    disposition: 'inline' | 'attachment',
    fresh?: boolean,
  ): Promise<Asset>;
};

/** Visible private media only; context changes, revoked sessions and late replies stay isolated. */
export function useHqMedia(
  repository: MediaRepository,
  id: string,
  manifestId: string,
  eager = false,
) {
  const host = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(eager);
  const [revision, setRevision] = useState(0);
  const [locked, setLocked] = useState(false);
  const [state, setState] = useState<{
    repository: MediaRepository;
    id: string;
    manifestId: string;
    asset: Asset | null;
    failed: boolean;
  } | null>(null);
  const attempt = useRef({ repository, id, manifestId, renewed: false });
  if (
    attempt.current.repository !== repository ||
    attempt.current.id !== id ||
    attempt.current.manifestId !== manifestId
  )
    attempt.current = { repository, id, manifestId, renewed: false };
  const renew = useCallback(() => {
    if (attempt.current.renewed) {
      setState((current) => (current ? { ...current, asset: null, failed: true } : current));
      return;
    }
    attempt.current.renewed = true;
    setRevision((value) => value + 1);
  }, []);
  const retry = useCallback(() => {
    attempt.current.renewed = true;
    setRevision((value) => value + 1);
  }, []);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      setLocked(true);
      setState(null);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    if (visible || eager) return;
    if (!host.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '80px' },
    );
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [visible, eager]);
  useEffect(() => {
    if (locked || !(visible || eager)) return;
    let active = true;
    const context = { repository, id, manifestId };
    setState({ ...context, asset: null, failed: false });
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      active = false;
    });
    void repository
      .open(id, manifestId, 'inline', attempt.current.renewed)
      .then((asset) => {
        if (active) setState({ ...context, asset, failed: false });
      })
      .catch(() => {
        if (active) setState({ ...context, asset: null, failed: true });
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [repository, id, manifestId, visible, eager, revision, locked]);
  const current =
    !locked &&
    state?.repository === repository &&
    state.id === id &&
    state.manifestId === manifestId
      ? state
      : null;
  return { host, asset: current?.asset ?? null, failed: current?.failed ?? false, renew, retry };
}
